import { useMemo, useState } from 'react';

import { useGetRunningTasksQuery } from '../../api/variantApi';
import type { RunningTask } from '../../variant_workflow/redux';
import { RUNNING_TASK_POLLING_INTERVAL } from '../constants';

export type RunningTaskStatusFilter = 'all' | RunningTask['status'];

type RunningTaskSearch = {
  freeText: string;
  errorText: string;
};

export const useRunningTasks = (triggeredBy?: string[]) => {
  const {
    data = [],
    isLoading,
    isSuccess,
  } = useGetRunningTasksQuery(
    { triggered_by_filter: triggeredBy },
    {
      pollingInterval: RUNNING_TASK_POLLING_INTERVAL,
    },
  );

  // Optimistic tasks (e.g. a just-triggered refresh) are written via
  // updateQueryData('getRunningTasks', undefined, ...) into the *unfiltered*
  // cache entry. Since this page queries with filter args, it has its own
  // cache entry and would not see them until the next poll returns the real
  // row. Merge them in here to restore the previous instant visibility.
  const { optimisticTasks } = useGetRunningTasksQuery(undefined, {
    selectFromResult: ({ data: allTasks }) => ({
      optimisticTasks: (allTasks ?? []).filter((task) => task.is_optimistic),
    }),
  });

  const tasks = useMemo(() => {
    const merged = [...data];
    for (const task of optimisticTasks ?? []) {
      if (merged.some((existing) => existing.task_id === task.task_id)) {
        continue;
      }
      if (triggeredBy?.length && !triggeredBy.includes(task.username)) {
        continue;
      }
      merged.unshift(task);
    }
    return merged;
  }, [data, optimisticTasks, triggeredBy]);

  const [statusFilter, setStatusFilter] =
    useState<RunningTaskStatusFilter>('all');
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [search, setSearch] = useState<RunningTaskSearch>({
    freeText: '',
    errorText: '',
  });

  const availableTaskTypes = useMemo(
    () => Array.from(new Set(tasks.map((task) => task.task_type))).sort(),
    [tasks],
  );

  const filteredTasks = useMemo(() => {
    const freeText = search.freeText.trim().toLowerCase();
    const errorText = search.errorText.trim().toLowerCase();

    return tasks.filter((task) => {
      if (statusFilter !== 'all' && task.status !== statusFilter) {
        return false;
      }
      if (taskTypeFilter !== 'all' && task.task_type !== taskTypeFilter) {
        return false;
      }
      if (freeText) {
        const haystack = [
          task.task_id,
          task.description,
          task.username,
          task.variant_hgvs ?? '',
          task.variant_id ? String(task.variant_id) : '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(freeText)) {
          return false;
        }
      }
      if (
        errorText &&
        !(task.error_message ?? '').toLowerCase().includes(errorText)
      ) {
        return false;
      }
      return true;
    });
  }, [tasks, statusFilter, taskTypeFilter, search]);

  return {
    tasks,
    filteredTasks,
    isLoading,
    isSuccess,
    availableTaskTypes,
    statusFilter,
    setStatusFilter,
    taskTypeFilter,
    setTaskTypeFilter,
    search,
    setSearch,
  };
};
